import classNames from 'classnames';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import Col from '../../common/Col';
import {GameLogicError} from '../errors';
import {Actions} from '../gameSlice';
import {useCurrentJeopardyWheel, useGameStatus, useGrowAnimationYear} from '../hooks';
import {EventId, getEvent} from '../resources/events';
import {JeopardyWheelId, useJeopardyWheels} from '../resources/jeopardies';
import {Event, JeopardyWheel} from '../types';
import Choice from './Choice';
import MessageBox from './MessageBox';
import WheelOfFate from './WheelOfFate';

const INITIAL_DELAY = 1500; // ms

const RESULT_VIEW_TIMEOUT = 1000; // ms

const Jeopardies = () => {
  const dispatch = useDispatch();
  const gameStatus = useGameStatus();
  const jeopardyWheels = useJeopardyWheels(gameStatus);

  const [delayAwaited, setDelayAwaited] = useState(false);
  const [acknowledgedWheels, setAcknowledgedWheels] = useState<JeopardyWheelId[]>([]);
  const yearEvents = gameStatus.history[gameStatus.currentYear].events;

  const currentUnspanWheel = useCurrentJeopardyWheel();

  const [currentJeopardyWheel, currentEvent] = useMemo<[JeopardyWheel | undefined, Event | undefined]>(() => {
    for (let index = 0; index < jeopardyWheels.length; index++) {
      const currentJeopardyWheel = jeopardyWheels[index];
      if (currentJeopardyWheel === currentUnspanWheel) return [currentJeopardyWheel, undefined];
      if (!acknowledgedWheels.includes(currentJeopardyWheel.id)) {
        const currentEventLog = yearEvents.find(eventLog => currentJeopardyWheel.eventsIds.includes(eventLog.id));
        const currentEvent = getEvent(GameLogicError.assertNonNullish(currentEventLog?.id));
        return [currentJeopardyWheel, currentEvent];
      }
    }
    return [undefined, undefined];
  }, [jeopardyWheels, currentUnspanWheel, acknowledgedWheels, yearEvents]);

  const handleConfirm = useCallback(() => {
    if (currentJeopardyWheel) setAcknowledgedWheels(prevState => [...prevState, currentJeopardyWheel.id]);
  }, [currentJeopardyWheel]);

  const handleChosen = useCallback(
    (eventId: EventId) => {
      setTimeout(() => dispatch(Actions.executeEvent(eventId)), RESULT_VIEW_TIMEOUT);
    },
    [dispatch]
  );

  const animationYear = useGrowAnimationYear(); // Used to determined if a delay is needed

  useEffect(() => {
    const timeout = setTimeout(() => setDelayAwaited(true), animationYear >= 0 ? INITIAL_DELAY : 0);
    return () => clearTimeout(timeout);
  }, [animationYear, currentJeopardyWheel, delayAwaited, dispatch, gameStatus.crop]);

  // Jump to Events phase if there are no more wheels (after delay if necessary)
  useEffect(() => {
    if (!currentJeopardyWheel && delayAwaited) dispatch(Actions.startEventsPhase());
  }, [currentJeopardyWheel, delayAwaited, dispatch]);

  if (!currentJeopardyWheel || !delayAwaited) return null;
  return (
    <MessageBox
      bottomIsTall
      bottom={
        <div
          className={classNames(
            'flex w-full justify-center items-center',
            currentEvent ? '-mt-[10rem] mb-md' : '-mt-[18rem]'
          )}
        >
          {currentEvent ? (
            <Choice
              className="px-12 py-4"
              title={currentEvent.name}
              description={currentEvent.description}
              buttonMessage="Continue"
              onConfirm={handleConfirm}
              selected={true}
              style={{width: '75%'}}
            />
          ) : (
            <WheelOfFate
              choices={currentJeopardyWheel.eventsIds}
              wheelClass={currentJeopardyWheel.wheelClass}
              onChosen={handleChosen}
              oneShot
            />
          )}
        </div>
      }
    >
      <div className={classNames('text-center mt-sm mx-md', currentEvent ? 'mb-[10rem]' : 'mb-[17rem]')}>
        <Col className="space-y-4">
          <div className="text-xl font-semibold leading-tight mobile:text-lg">{currentJeopardyWheel.description}</div>
          <div className="text-huge font-semibold leading-tight mobile:text-3xl">
            {currentEvent ? currentJeopardyWheel.resultTitle : currentJeopardyWheel.title}
          </div>
        </Col>
      </div>
    </MessageBox>
  );
};

export default Jeopardies;
